import { optimizedImage } from "./images";
import type { BlogCategoryRow, BlogPostRow } from "./blog";

/**
 * Blog list data fetched through the plain REST endpoint so it can run inside
 * a route loader on the server (SSR) as well as during client navigation.
 * Rendering the list on the server puts the first cover image straight into
 * the HTML, which removes ~2.8s of LCP "resource load delay" on mobile.
 */
export type BlogListPost = Pick<
  BlogPostRow,
  | "id"
  | "slug"
  | "cover_image_url"
  | "external_url"
  | "published_at"
  | "reading_time_minutes"
  | "author_name"
  | "category_id"
  | "title_en"
  | "title_pl"
  | "title_by"
  | "excerpt_en"
  | "excerpt_pl"
  | "excerpt_by"
> & { blog_categories: BlogCategoryRow | null };

const POST_FIELDS =
  "id,slug,cover_image_url,external_url,published_at,reading_time_minutes,author_name,category_id,title_en,title_pl,title_by,excerpt_en,excerpt_pl,excerpt_by,blog_categories(*)";

export async function fetchBlogList(): Promise<{
  posts: BlogListPost[];
  categories: BlogCategoryRow[];
}> {
  const url = import.meta.env["VITE_SUPABASE_URL"];
  const key = import.meta.env["VITE_SUPABASE_PUBLISHABLE_KEY"];
  const headers = { apikey: key, Authorization: `Bearer ${key}` };
  const now = new Date().toISOString();

  const [postsRes, categoriesRes] = await Promise.all([
    fetch(
      `${url}/rest/v1/blog_posts?select=${encodeURIComponent(POST_FIELDS)}&published_at=lte.${now}&published_at=not.is.null&order=published_at.desc`,
      { headers },
    ),
    fetch(`${url}/rest/v1/blog_categories?select=*&order=name_en.asc`, { headers }),
  ]);

  if (!postsRes.ok || !categoriesRes.ok) {
    return { posts: [], categories: [] };
  }

  return {
    posts: (await postsRes.json()) as BlogListPost[],
    categories: (await categoriesRes.json()) as BlogCategoryRow[],
  };
}

/**
 * Preload the first cover image so the browser discovers the LCP candidate
 * from the HTML head instead of after React renders the grid.
 */
export function blogPreloadLinks(data?: { posts: BlogListPost[] }) {
  const first = data?.posts?.[0]?.cover_image_url;
  if (!first) return [];
  return [
    {
      rel: "preload",
      as: "image",
      href: optimizedImage(first, 380),
      imageSrcSet: `${optimizedImage(first, 280)} 280w, ${optimizedImage(first, 380)} 380w, ${optimizedImage(first, 560)} 560w, ${optimizedImage(first, 760)} 760w`,
      imageSizes: "(max-width: 768px) calc(100vw - 48px), (max-width: 1024px) calc(50vw - 36px), 368px",
      fetchPriority: "high" as const,
    },
  ];
}
